// Per-email copy for the emails deliverable (meeting 24.7. D12): the client
// pastes each e-mail into their own mail tool, so every e-mail section gets
// a "Zkopírovat text" button and the copied text is PLAIN text - markdown
// control syntax stripped, content kept word for word.
//
// The generator contract (dfy2 references, MarkdownView) shapes the emails
// document as `## heading` sections; e-mail bodies may sit inside
// `??? Title ... ???` folds. Both shapes split here: headings first, and a
// document with only folds falls back to one section per fold.

import { isDocHtml } from "./DocHtml";
import { buildDocContext } from "./doc/doc-context";
import { safeDocId } from "./doc/doc-parse";
import { docHtmlToPlainText } from "./doc/doc-plain";
import type { SegmentMap } from "./doc/doc-segments";

// Re-exported: every existing call site imports these from this module, and
// the clipboard write plus the authored-HTML conversion now live where the
// document renderer can reach them too (see doc/doc-plain.ts, clipboard.ts).
export { copyPlainText } from "./clipboard";
export { docHtmlToPlainText };
export { buildDocContext };

export interface EmailSection {
  title: string;
  md: string;
  /**
   * The fold's own id, carried onto the rendered section. The split UNWRAPS
   * the fold, so without this the timeline's jump target would exist in the
   * whole-document reader and vanish in the e-mails view: the same beat would
   * be a button in one place and dead in the other.
   */
  id?: string;
  /** Segment the fold declared, so the section header can carry its colour. */
  seg?: string;
}

export interface EmailSplit {
  intro: string;
  sections: EmailSection[];
  /** True when the document is authored HTML - sections are HTML fragments. */
  html?: boolean;
}

const HEADING = /^(#{1,2})\s+(.+)$/;
const FOLD_OPEN = /^\?\?\?\s+(.+)$/;
const FOLD_CLOSE = /^\?\?\?\s*$/;

function cleanInline(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/==([^=\s][^=]*)==/g, "$1")
    .replace(/\*([^*\s][^*]*)\*/g, "$1")
    .replace(/\s*\{\.lead\}\s*$/, "")
    .trim();
}

/** Authored-HTML emails doc: every `<fold title="...">` is one e-mail. The
 * split works on the SOURCE so each fragment can be re-rendered by the same
 * renderer; the intro (lead, calendar, strategy chapters) keeps everything
 * that is not a fold. */
function splitDocHtmlSections(source: string): EmailSplit {
  const sections: EmailSection[] = [];
  const foldRe = /<fold\b([^>]*)>([\s\S]*?)<\/fold>/gi;
  const attr = (raw: string, name: string) =>
    new RegExp(`${name}\\s*=\\s*"([^"]*)"`, "i").exec(raw)?.[1]?.trim() || undefined;
  const intro = source.replace(foldRe, (whole, rawAttrs: string, inner: string) => {
    sections.push({
      title: cleanInline(attr(rawAttrs, "title") ?? ""),
      md: inner.trim(),
      id: safeDocId(attr(rawAttrs, "id")),
      seg: attr(rawAttrs, "seg")
    });
    return "";
  });
  if (sections.length === 0) return { intro: source, sections, html: true };
  // Chapters that lost all their folds would render as empty openers.
  const cleanedIntro = intro.replace(/<chapter\b[^>]*>\s*<\/chapter>/gi, "");
  return { intro: cleanedIntro.trim(), sections, html: true };
}

export function splitEmailSections(md: string): EmailSplit {
  if (isDocHtml(md)) return splitDocHtmlSections(md);
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const sections: EmailSection[] = [];
  const intro: string[] = [];
  let current: { title: string; lines: string[] } | null = null;
  let inFold = false;
  const flush = () => {
    if (current) sections.push({ title: cleanInline(current.title), md: current.lines.join("\n").trim() });
    current = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    // A `##` inside an open fold belongs to the e-mail body, not the split.
    if (inFold && FOLD_CLOSE.test(line)) inFold = false;
    else if (!inFold && FOLD_OPEN.test(line)) inFold = true;
    const heading = inFold ? null : HEADING.exec(line);
    if (heading) {
      // A single `#` before any section is the document title, not an e-mail.
      if (heading[1] === "#" && sections.length === 0 && !current) {
        intro.push(line);
        continue;
      }
      flush();
      current = { title: heading[2], lines: [] };
      continue;
    }
    if (current) current.lines.push(raw);
    else intro.push(raw);
  }
  flush();
  if (sections.length > 0) return { intro: intro.join("\n").trim(), sections };
  // No headings: one section per top-level fold (older generator shape).
  const foldSections: EmailSection[] = [];
  const rest: string[] = [];
  let fold: { title: string; lines: string[] } | null = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (fold) {
      if (FOLD_CLOSE.test(line)) {
        foldSections.push({ title: cleanInline(fold.title), md: fold.lines.join("\n").trim() });
        fold = null;
      } else {
        fold.lines.push(raw);
      }
      continue;
    }
    const open = FOLD_OPEN.exec(line);
    if (open) {
      fold = { title: open[1], lines: [] };
      continue;
    }
    rest.push(raw);
  }
  if (fold !== null) {
    const open = fold as { title: string; lines: string[] };
    foldSections.push({ title: cleanInline(open.title), md: open.lines.join("\n").trim() });
  }
  return { intro: rest.join("\n").trim(), sections: foldSections };
}

// Markdown -> plain text the client can paste into a mail tool. Strips the
// reader syntax (folds, callouts, stats, headings, bold, quotes, tables)
// while keeping every word of content in reading order.
export function markdownToPlainText(md: string, segments?: SegmentMap): string {
  if (isDocHtml(md)) return docHtmlToPlainText(md, segments);
  const out: string[] = [];
  // The whole `::: kind` fence family flattens to `Label: Value (note)`
  // lines; `srovnani` keeps its column headers and items in reading order.
  let fence: string | null = null;
  for (const raw of md.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    const fenceOpen = /^:::\s*(\w+)\s*(.*)$/.exec(line);
    if (fence === null && fenceOpen && ["stats", "cislo", "srovnani", "kroky", "mereni"].includes(fenceOpen[1])) {
      fence = fenceOpen[1];
      if (fence === "srovnani" && fenceOpen[2]) {
        out.push(fenceOpen[2].split("|").map((part) => cleanInline(part)).filter(Boolean).join(" / "));
      }
      continue;
    }
    if (fence !== null) {
      if (/^:::\s*$/.test(line)) { fence = null; continue; }
      if (fence === "srovnani") {
        const item = line.trim();
        if (!item) continue;
        if (/^-{3,}$/.test(item)) { out.push(""); continue; }
        out.push(`- ${cleanInline(item.replace(/^[-*]\s+/, ""))}`);
        continue;
      }
      const parts = line.split("|").map((part) => part.trim()).filter(Boolean);
      if (parts.length >= 2) out.push(`${cleanInline(parts[0])}: ${cleanInline(parts[1])}${parts[2] ? ` (${cleanInline(parts[2])})` : ""}`);
      continue;
    }
    if (FOLD_CLOSE.test(line)) continue;
    const fold = FOLD_OPEN.exec(line);
    if (fold) { out.push(cleanInline(fold[1])); continue; }
    const callout = /^!!!\s+\w+\s+(.+)$/.exec(line);
    if (callout) { out.push(cleanInline(callout[1])); continue; }
    const heading = /^#{1,4}\s+(.+)$/.exec(line);
    if (heading) { out.push(cleanInline(heading[1])); continue; }
    // `*** Kicker` divider: the label survives, the rule does not.
    const divider = /^\*{3,}\s*(.*)$/.exec(line);
    if (divider) { if (divider[1]) out.push(cleanInline(divider[1])); continue; }
    if (/^\s*\|/.test(line)) {
      const cells = line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((cell) => cleanInline(cell));
      if (cells.every((cell) => /^:?-{2,}:?$/.test(cell))) continue;
      out.push(cells.filter(Boolean).join(" | "));
      continue;
    }
    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) { out.push(cleanInline(quote[1])); continue; }
    const bullet = /^(\s*)[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      // Checklist boxes (`[ ]` / `[x]`) are reader chrome, not content.
      const content = bullet[2].replace(/^\[( |x|X)\]\s+/, "");
      out.push(`${bullet[1]}- ${cleanInline(content)}`);
      continue;
    }
    const numbered = /^(\s*)(\d+)[.)]\s+(.*)$/.exec(line);
    if (numbered) { out.push(`${numbered[1]}${numbered[2]}. ${cleanInline(numbered[3])}`); continue; }
    out.push(cleanInline(line));
  }
  // Collapse runs of blank lines: paragraphs stay separated, nothing gapes.
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
